import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Images from '../../assests/images/Images';
import CtaButton from '../../components/common/CtaButton';

/* Directional Scroll & Page Load Reveal Hook */
const useScrollReveal = () => {
  useEffect(() => {
    const isReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const elements = document.querySelectorAll(
      '.sipl-reveal, .sipl-reveal-ltr, .sipl-reveal-rtl, .sipl-reveal-ttb, .sipl-reveal-btt'
    );

    if (isReduced) {
      elements.forEach((el) => el.classList.add('sipl-revealed'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('sipl-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -30px 0px' }
    );

    elements.forEach((el) => observer.observe(el));

    const checkScrollPosition = () => {
      elements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top <= (window.innerHeight || document.documentElement.clientHeight) - 20) {
          el.classList.add('sipl-revealed');
        }
      });
    };

    checkScrollPosition();
    window.addEventListener('scroll', checkScrollPosition, { passive: true });

    return () => {
      window.removeEventListener('scroll', checkScrollPosition);
      observer.disconnect();
    };
  }, []);
};

const DYNAMIC_MESSAGES = [
  { text: 'Human-centered UI/UX design systems.', color: '#0d9488' },
  { text: 'Clickable Figma prototypes for early validation.', color: '#f97316' },
  { text: 'Reusable token libraries for fast dev handoff.', color: '#1a2f6e' },
  { text: 'WCAG 2.1 accessible web & mobile interfaces.', color: '#4caf50' },
];

const MARQUEE_ITEMS = [
  { label: 'Figma Design Tokens', color: '#0d9488' },
  { label: 'Clickable Interactive Prototypes', color: '#f97316' },
  { label: 'WCAG 2.1 Accessibility', color: '#4caf50' },
  { label: 'User Persona Journeys', color: '#1a2f6e' },
  { label: 'Dark / Light Themes', color: '#0d9488' },
  { label: 'Micro-Interactions', color: '#f97316' },
  { label: 'Tailwind & Bootstrap Specs', color: '#1a2f6e' },
  { label: 'Fast Developer Asset Handoff', color: '#4caf50' },
];

const CAPABILITIES = [
  {
    id: 'ux-research',
    num: '01',
    icon: '🎨',
    title: 'UX Research & Journeys',
    fullTitle: 'UX Research & User Journeys',
    subtitle: 'Information architecture, empathy mapping & usability testing',
    desc: 'Conduct empathy mapping, user interviews, information architecture mapping, and customer journey flows to eliminate friction points across your application.',
    deliverables: [
      'User Personas & Customer Scenarios',
      'Information Architecture (IA) Blueprints',
      'Usability & Heuristic UX Audits',
      'Frictionless Conversion Journey Mapping',
    ],
    tags: ['User Personas', 'Journey Mapping', 'IA Specs', 'Usability Audits'],
    accent: '#0d9488',
  },
  {
    id: 'ui-design',
    num: '02',
    icon: '✨',
    title: 'UI & Interactions',
    fullTitle: 'UI & Interaction Design',
    subtitle: 'Modern layouts, dark/light themes & micro-interactions',
    desc: 'Craft visually stunning interface layouts with modern typography scales, adaptive dark/light theme modes, and subtle micro-interactions that delight users.',
    deliverables: [
      'Responsive Web & Mobile Layout Screens',
      'Adaptive Dark / Light Theme Styling',
      'Micro-Interactions & Animated UI States',
      'Visual Hierarchy & Typography Scaling',
    ],
    tags: ['Responsive UI', 'Dark/Light Themes', 'Micro-Interactions', 'Visual Hierarchy'],
    accent: '#f97316',
  },
  {
    id: 'design-systems',
    num: '03',
    icon: '📐',
    title: 'Figma Design Systems',
    fullTitle: 'Figma Design Systems & Tokens',
    subtitle: 'Scalable component libraries & developer handoff',
    desc: 'Build synchronized Figma design token libraries and component kits matching Tailwind CSS or Bootstrap 5 utility classes for developer handoff.',
    deliverables: [
      'Synchronized Figma Component Libraries',
      'Color, Typography & Spacing Design Tokens',
      'WCAG 2.1 AA Accessibility Guidelines',
      'Developer Asset Handoff Documentation',
    ],
    tags: ['Color Tokens', 'Figma Libraries', 'WCAG 2.1', 'Dev Handoff'],
    accent: '#1a2f6e',
  },
  {
    id: 'prototyping',
    num: '04',
    icon: '📱',
    title: 'Interactive Prototypes',
    fullTitle: 'Interactive Figma Wireframes & Prototypes',
    subtitle: 'Clickable prototypes for early stakeholder feedback',
    desc: 'Build clickable user prototypes allowing stakeholders to test interactions, experience real user flows, and refine product concepts before writing code.',
    deliverables: [
      'Low & High-Fidelity Wireframes',
      'Clickable Interactive Figma Prototypes',
      'Stakeholder & User Flow Validation',
      'Motion Design & Screen Transition Specs',
    ],
    tags: ['Low/High Fidelity', 'Clickable Figma Specs', 'User Testing', 'Motion Design'],
    accent: '#4caf50',
  },
];

const AnimatedText = ({ phrases }) => {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex((prev) => (prev + 1) % phrases.length);
        setVisible(true);
      }, 300);
    }, 2600);
    return () => clearInterval(timer);
  }, [phrases]);

  const current = phrases[index];

  return (
    <span
      style={{
        display: 'inline-block',
        color: current.color,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(-6px)',
        transition: 'opacity 0.45s ease, transform 0.45s ease, color 0.45s ease',
        fontWeight: 700,
      }}
    >
      {current.text}
    </span>
  );
};

const ProductDesignPage = () => {
  const [activeTabId, setActiveTabId] = useState('ux-research');
  const [isChangingTab, setIsChangingTab] = useState(false);

  useEffect(() => {
    document.title = 'UI/UX & Product Design | Seikural Innovation';
    window.scrollTo(0, 0);
  }, []);

  useScrollReveal();

  const handleTabChange = (id) => {
    if (id === activeTabId) return;
    setIsChangingTab(true);
    setTimeout(() => {
      setActiveTabId(id);
      setIsChangingTab(false);
    }, 220);
  };

  const activeCapability = CAPABILITIES.find((c) => c.id === activeTabId) || CAPABILITIES[0];

  return (
    <div className="sipl-home-page-wrapper text-dark overflow-hidden">
      {/* Ambient background glow spots (Logo color palette) */}
      <div className="sipl-ambient-glow" style={{ position: 'absolute', pointerEvents: 'none', top: '1%', left: '-4%', width: 550, height: 550, background: 'radial-gradient(circle, rgba(13,148,136,0.045) 0%, transparent 70%)' }} />
      <div className="sipl-ambient-glow" style={{ position: 'absolute', pointerEvents: 'none', top: '30%', right: '-6%', width: 600, height: 600, background: 'radial-gradient(circle, rgba(249,115,22,0.04) 0%, transparent 70%)' }} />
      <div className="sipl-ambient-glow" style={{ position: 'absolute', pointerEvents: 'none', top: '70%', left: '-5%', width: 500, height: 500, background: 'radial-gradient(circle, rgba(26,47,110,0.035) 0%, transparent 70%)' }} />

      {/* ── 1. HERO SECTION ── */}
      <section
        className="border-bottom sipl-section-transparent sipl-hero"
        style={{
          position: 'relative',
          zIndex: 1,
          overflow: 'hidden',
          background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 60%, #ffffff 100%)',
          minHeight: 'calc(100vh - 80px)',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div className="container-fluid px-3 px-sm-4 px-lg-5 py-4" style={{ position: 'relative', zIndex: 1, maxWidth: '1680px' }}>
          <div className="row align-items-center g-4 g-xl-5">

            {/* Left Column Text */}
            <div className="col-12 col-lg-5">
              <span
                className="text-uppercase fw-bold d-block mb-2 sipl-reveal-ttb"
                style={{ color: '#0284c7', fontSize: '0.8rem', letterSpacing: '0.12em', '--gr-delay': '120ms' }}
              >
                UI/UX &amp; PRODUCT DESIGN
              </span>

              <h1
                className="mb-3 sipl-reveal-ttb"
                style={{
                  fontSize: 'clamp(1.8rem, 2.85vw, 2.45rem)',
                  letterSpacing: '-0.025em',
                  lineHeight: 1.22,
                  color: '#0f172a',
                  fontWeight: 600,
                  '--gr-delay': '180ms',
                }}
              >
                <span className="d-block" style={{ color: '#0f172a', fontWeight: 600 }}>UI/UX &amp; Product</span>
                <span className="d-block" style={{ color: '#0f172a', fontWeight: 600 }}>Design</span>
              </h1>

              <div className="mb-3" style={{ minHeight: 36 }}>
                <AnimatedText phrases={DYNAMIC_MESSAGES} />
              </div>

              <div
                className="mb-4 sipl-reveal-ttb"
                style={{ paddingLeft: '0', '--gr-delay': '320ms' }}
              >
                <p
                  className="mb-0"
                  style={{
                    fontSize: '0.96rem',
                    lineHeight: 1.68,
                    maxWidth: 540,
                    color: '#475569',
                  }}
                >
                  Transform complex operational workflows into clean, intuitive, and conversion-focused product interfaces.
                </p>
              </div>

              <div
                className="d-flex flex-wrap align-items-center gap-3 mb-4 sipl-reveal-ttb"
                style={{ '--gr-delay': '460ms' }}
              >
                <CtaButton
                  to="/contact"
                  variant="primary"
                >
                  Discuss Product Design
                </CtaButton>
                <CtaButton
                  href="#capabilities"
                  variant="secondary"
                  arrowDirection="down"
                >
                  View Design Services
                </CtaButton>
              </div>

              <div
                className="sipl-reveal-ttb"
                style={{
                  fontSize: '0.84rem',
                  color: '#64748b',
                  fontWeight: 500,
                  letterSpacing: '-0.01em',
                  '--gr-delay': '600ms',
                }}
              >
                User Research · Wireframing · Prototyping · Design Systems
              </div>
            </div>

            {/* Right Column Image */}
            <div
              className="col-12 col-lg-7 d-flex align-items-center justify-content-center sipl-reveal-ttb"
              style={{ '--gr-delay': '350ms' }}
            >
              <div
                className="w-100 position-relative"
                style={{
                  height: 'clamp(260px, 38vw, 440px)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  backgroundColor: '#f8fafc',
                  border: '1px solid rgba(226, 232, 240, 0.8)',
                  boxShadow: '0 16px 36px -10px rgba(15, 23, 42, 0.16), 0 4px 12px rgba(15, 23, 42, 0.06)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {Images?.uiuxheader ? (
                  <img
                    src={Images.uiuxheader}
                    alt="UI/UX and Product Design"
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }}
                  />
                ) : (
                  <span style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Image coming soon</span>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>


      {/* ── 2. CONTINUOUS INFINITE SCROLL TICKER MARQUEE STRIP ── */}
      <div className="sipl-marquee-container bg-white border-top border-bottom py-2.5">
        <div className="sipl-marquee-track">
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, idx) => (
            <span
              key={idx}
              className="badge bg-light text-dark border px-3.5 py-2 rounded-pill font-weight-semibold mx-1"
              style={{ fontSize: '0.82rem', color: '#334155' }}
            >
              <span style={{ color: item.color, marginRight: 6 }}>●</span> {item.label}
            </span>
          ))}
        </div>
      </div>

      {/* ── 3. SEGMENTED TAB SELECTOR + ANIMATED DETAILS PANEL BELOW ── */}
      <section id="capabilities" className="py-5 bg-light">
        <div className="container py-4">
          {/* Section Header Centered */}
          <div className="text-center mb-4" style={{ maxWidth: 640, margin: '0 auto' }}>
            <span className="text-uppercase fw-bold small" style={{ color: '#1a2f6e', letterSpacing: '0.08em' }}>
              DESIGN SERVICES
            </span>
            <h2 className="h3 fw-bold text-dark mb-2">What We Design</h2>
            <p className="text-muted small mb-0">Select any design domain below to inspect deliverables and tooling specs.</p>
          </div>

          {/* SINGLE ROW SEGMENTED CONTROL WRAPPER */}
          <div className="mb-5 sipl-tabs-container" style={{ textAlign: 'center', overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '4px' }}>
            <div
              role="tablist"
              style={{ display: 'inline-flex', flexWrap: 'nowrap', alignItems: 'center', gap: '6px', padding: '6px', backgroundColor: '#ffffff', borderRadius: '999px', boxShadow: '0 1px 4px rgba(15,23,42,0.08)', border: '1px solid #e2e8f0' }}
            >
              {CAPABILITIES.map((tab) => {
                const isActive = activeTabId === tab.id;
                return (
                  <button
                    type="button"
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`btn rounded-pill px-3 py-2 fw-bold text-nowrap d-inline-flex align-items-center gap-2`}
                    style={{
                      backgroundColor: isActive ? '#1a2f6e' : 'transparent',
                      color: isActive ? '#ffffff' : '#475569',
                      fontSize: '0.86rem',
                      border: 'none',
                      transform: isActive ? 'scale(1.03)' : 'scale(1)',
                      transition: 'all 0.3s ease',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <span style={{ fontSize: '0.95rem' }}>{tab.icon}</span>
                    <span>{tab.title}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ACTIVE CAPABILITY DETAILS PANEL WITH LONGER SMOOTH TRANSITION */}
          <div className="mx-auto" style={{ maxWidth: 940 }}>
            <div
              className="card border shadow-sm rounded-4 p-4 p-md-5 bg-white sipl-card-hover"
              style={{
                borderTop: `4px solid #1a2f6e`,
                minHeight: '380px',
                opacity: isChangingTab ? 0 : 1,
                transform: isChangingTab ? 'translateY(12px)' : 'translateY(0)',
                transition: 'opacity 0.35s ease, transform 0.35s ease',
              }}
            >
              {/* Header Bar */}
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4 pb-3 border-bottom">
                <div className="d-flex align-items-center gap-3">
                  <span
                    className="rounded-circle d-flex align-items-center justify-content-center fw-bold fs-4"
                    style={{
                      width: 50,
                      height: 50,
                      backgroundColor: `${activeCapability.accent}15`,
                      color: activeCapability.accent,
                      transition: 'all 0.4s ease',
                    }}
                  >
                    {activeCapability.icon}
                  </span>
                  <div>
                    <div className="d-flex align-items-center gap-2">
                      <span
                        className="badge px-2.5 py-1 rounded-pill fw-bold text-uppercase"
                        style={{ backgroundColor: `${activeCapability.accent}15`, color: activeCapability.accent, fontSize: '0.72rem' }}
                      >
                        {activeCapability.num} / DESIGN CAPABILITY
                      </span>
                    </div>
                    <h3 className="h4 fw-bold text-dark mb-0 mt-1">{activeCapability.fullTitle}</h3>
                    <div className="text-muted small">{activeCapability.subtitle}</div>
                  </div>
                </div>

                <Link
                  to="/contact"
                  className="btn px-4 py-2 rounded-3 fw-bold text-white shadow-sm sipl-arrow-link"
                  style={{ backgroundColor: '#1a2f6e', border: 'none', fontSize: '0.85rem', transition: 'all 0.3s ease' }}
                >
                  <span>Request Audit</span> <span className="ms-1">→</span>
                </Link>
              </div>

              {/* Description */}
              <p className="text-muted small mb-4" style={{ lineHeight: 1.7, fontSize: '0.94rem' }}>
                {activeCapability.desc}
              </p>

              {/* Deliverables 2x2 Grid with Interactive Hover Float */}
              <div className="mb-4">
                <div className="text-uppercase fw-bold mb-3" style={{ fontSize: '0.7rem', letterSpacing: '0.08em', color: activeCapability.accent }}>
                  Key Design Deliverables
                </div>
                <div className="row g-3">
                  {activeCapability.deliverables.map((del) => (
                    <div key={del} className="col-12 col-sm-6">
                      <div
                        className="d-flex align-items-center gap-3 p-3 rounded-3 bg-light border h-100 sipl-card-hover"
                        style={{ transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)' }}
                      >
                        <span
                          className="rounded-circle d-inline-flex align-items-center justify-content-center fw-bold flex-shrink-0"
                          style={{
                            width: 24,
                            height: 24,
                            backgroundColor: `${activeCapability.accent}15`,
                            color: activeCapability.accent,
                            fontSize: '0.78rem',
                          }}
                        >
                          ✓
                        </span>
                        <span className="small text-dark fw-medium" style={{ fontSize: '0.85rem' }}>{del}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tech Stack Strip */}
              <div className="pt-3.5 border-top d-flex flex-wrap align-items-center gap-2">
                <span className="text-uppercase fw-bold text-muted me-1" style={{ fontSize: '0.68rem', letterSpacing: '0.08em' }}>Toolkit:</span>
                {activeCapability.tags.map((tag) => (
                  <span key={tag} className="badge bg-light text-dark border px-3 py-1.5 rounded-pill fw-semibold" style={{ fontSize: '0.78rem' }}>
                    <span style={{ color: activeCapability.accent, marginRight: 5 }}>●</span> {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 4. MINIMAL CTA BANNER ── */}
      <section className="py-5 bg-white border-top sipl-reveal-btt">
        <div className="container text-center py-2">
          <div className="max-w-xl mx-auto" style={{ maxWidth: 560 }}>
            <h2 className="h4 fw-bold text-dark mb-2">Elevate Your Product User Experience</h2>
            <p className="text-muted small mb-4">Transform complex user flows into intuitive digital interfaces.</p>
            <div className="d-flex flex-wrap justify-content-center gap-3">
              <CtaButton to="/contact" variant="primary">
                Talk to Our Designers
              </CtaButton>
              <CtaButton to="/services/web-development" variant="secondary">
                Explore All Services
              </CtaButton>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDesignPage;
